"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
export default function Introduction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section data-section-id="264445" id="introduction" className="bg-[#0B1220] py-20 h-auto relative overflow-hidden flex flex-col justify-center items-center scroll-mt-[120px]">
      <div ref={ref} className={`container mx-auto px-6 max-w-5xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12">
          <h1 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#F8FAFC] mb-6 leading-tight break-words mt-0">
            Privacy Policy
          </h1>
          
          <p className="font-default text-lg md:text-xl leading-loose text-[#CBD5E1] max-w-3xl mx-auto break-words mt-0">
            How Future Experts for Technical Consultancy handles website inquiries and business contact information.
          </p>
        </div>

        <Card className="bg-[#0F172A] border-[rgba(19,184,200,0.18)] shadow-xl overflow-hidden rounded-lg">
          <CardContent className="p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="p-3 bg-[#13B8C8]/10 rounded-lg">
                <ShieldCheck className="w-8 h-8 text-[#13B8C8]" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-[#F8FAFC] mb-4">Policy Overview</h2>
                <p className="text-[#CBD5E1] leading-loose">
                  At Future Experts for Technical Consultancy (FETC), we are committed to protecting your privacy and ensuring the security of your information. This policy outlines our practices regarding the collection, use, and safeguarding of data obtained through our website and business interactions. We implement reasonable administrative, technical, and organizational safeguards to protect your information against unauthorized access or disclosure.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}